import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Trash2, RefreshCcw,
  Tag, CheckCircle, Clock, XCircle, FileSearch, FileText,
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { partitionStatusFromColumns } from '../../shared/utils/erpListColumnOrder';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useTranslation } from 'react-i18next';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { makeModulePageTranslators } from '../../shared/utils/pageI18n';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import QawaemTableEmptyRow from '../../shared/components/table/QawaemTableEmptyRow';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import {
  ErpFieldLabel,
  ERP_MODAL_FORM_CLASS,
  erpFormLabelClass,
  resolveModalFormSections,
} from '../../shared/utils/erpFormFieldClasses';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useSettings } from '../../core/contexts/SettingsContext';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import {
  ALL_HR_DATA_REFRESH_EVENTS,
  dispatchHrEmployeesRefresh,
  dispatchHrRefreshForApiPath,
  useHrApiRefreshListener,
} from '../ressources-humaines/hrRefreshEvents';
import { EmployeePortalScopeAlert, EmployeePortalUnlinkedState } from './employeePortalShared';
import { usePortalEmployeeContext } from './PortalEmployeeContext';

function resolveApiError(error, fallback) {
  return error?.message || error?.response?.data?.error || fallback;
}

function exportPortalCsv(rows, columns, filename) {
  if (!rows.length) return;
  const keys = columns.map((c) => c.key);
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    keys.join(','),
    ...rows.map((row) => keys.map((k) => escape(row[k])).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Shared CRUD shell for Employee Portal module pages.
 * @param {object} props
 * @param {string} props.ns - i18n namespace suffix (modules.employee_portal.pages.{ns})
 * @param {import('react').ComponentType} props.icon - Page header icon
 * @param {string} props.apiPath - API base path e.g. /api/approbations/workflows
 * @param {Array} props.columns - Table column definitions
 * @param {Function} props.computeStats - (records) => stat card config array (capped to 4)
 * @param {object} props.emptyForm - Default form state
 * @param {Array} props.formSections - Modal sections with fields
 * @param {Function} [props.mapRecordToForm] - Map DB row to form
 * @param {Function} [props.mapFormToPayload] - Map form to API payload
 * @param {Function} [props.filterRecords] - Optional extra filter
 * @param {string} [props.codeField] - Entity reference field name (e.g. request_code)
 * @param {string} [props.entityPrefixKey] - settings.configuration.entityPrefixes key
 * @param {string} [props.codePrefixFallback] - Fallback prefix when settings key is absent
 * @param {boolean} [props.codeEditable] - Allow editing رمز in modal (default true)
 * @param {boolean} [props.allowCreate] - Show create button (default true)
 * @param {boolean} [props.allowEdit] - Show edit actions (default true)
 * @param {boolean} [props.allowDelete] - Show delete actions (default true)
 * @param {object} [props.apiQueryParams] - Query params for GET requests
 * @param {boolean} [props.requireEmployeeScope] - Skip fetch until portal employee is linked
 */
export default function EmployeePortalCrudShell({
  ns,
  icon: PageIcon,
  apiPath,
  columns,
  computeStats,
  emptyForm,
  formSections,
  mapRecordToForm,
  mapFormToPayload,
  filterRecords,
  codeField = 'code',
  entityPrefixKey = '',
  codePrefixFallback = 'REF',
  codeEditable = true,
  allowCreate = true,
  allowEdit = true,
  allowDelete = true,
  apiQueryParams = null,
  requireEmployeeScope = false,
  gradient = 'from-teal-500 to-teal-700',
  statusField = 'status',
  statusOptions: statusOptionsProp = null,
  normalizeStatus = null,
  mapStatusToPayload = null,
}) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { p, pc, pl } = makeModulePageTranslators(t, 'employee_portal', ns);
  const { isDashboardLightMode: isLight } = useGlobalTheme();
  const cfgTheme = useModuleConfigTheme();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const portal = usePortalEmployeeContext();
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: entityLabel });

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const {
    filters: columnFilters,
    setFilter: setColumnFilter,
    editingKey: columnFilterEditingKey,
    setEditingKey: setColumnFilterEditingKey,
  } = useColumnHeaderFilters();
  const { sections: modalSections, showTabs: showFormTabs } = useMemo(
    () => resolveModalFormSections(formSections),
    [formSections],
  );
  const defaultFormTab = modalSections[0]?.id || 'basic';
  const [formTab, setFormTab] = useState(defaultFormTab);
  const formTabs = useMemo(
    () => (showFormTabs ? modalSections.map((section) => ({
      id: section.id,
      label: p(section.titleKey),
      icon: section.icon || FileText,
    })) : []),
    [modalSections, showFormTabs, p],
  );
  const [formData, setFormData] = useState({ ...emptyForm });
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(columns.map((c) => [c.key, true]))
  );

  const toForm = mapRecordToForm || ((r) => ({ ...emptyForm, ...r }));
  const toPayload = mapFormToPayload || ((f) => ({ ...f }));

  const effectiveQueryParams = useMemo(() => {
    if (!requireEmployeeScope) return apiQueryParams;
    const merged = { ...(apiQueryParams || {}) };
    if (portal.employeeId && !merged.employee_id) {
      merged.employee_id = portal.employeeId;
    }
    return merged;
  }, [requireEmployeeScope, apiQueryParams, portal.employeeId]);

  const scopeReady = !requireEmployeeScope || (portal.profileResolved && portal.scopeReady);
  const scopeBlocked = requireEmployeeScope && portal.profileResolved && !portal.scopeReady;
  const scopeLoading = requireEmployeeScope && !portal.profileResolved;

  const loadRecords = async () => {
    if (!scopeReady) {
      setRecords([]);
      return;
    }
    try {
      setLoading(true);
      const response = await localApiAdapter.get(apiPath, effectiveQueryParams ? { params: effectiveQueryParams } : undefined);
      const data = response?.data ?? response;
      setRecords(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error(`Error loading ${apiPath}:`, error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRecords(); }, [refreshTrigger, apiPath, scopeReady, JSON.stringify(effectiveQueryParams || {})]);

  useHrApiRefreshListener(ALL_HR_DATA_REFRESH_EVENTS, () => {
    setRefreshTrigger((n) => n + 1);
  });

  const notifyPortalDataChanged = useCallback(() => {
    dispatchHrRefreshForApiPath(apiPath);
    if (requireEmployeeScope) dispatchHrEmployeesRefresh();
  }, [apiPath, requireEmployeeScope]);

  useEffect(() => {
    if (!showModal && !showViewModal) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showModal, showViewModal]);

  useEffect(() => {
    if (viewMode === 'table' && searchTerm) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateCode = useCallback(() => {
    if (!codeField) return '';
    const prefix = getEntityPrefix(settings, entityPrefixKey, codePrefixFallback);
    const list = Array.isArray(records) ? records : [];
    return generateEntityReference(prefix, list, (r) => r[codeField]);
  }, [settings, entityPrefixKey, codePrefixFallback, records, codeField]);

  const resetForm = (keepModalOpen = false) => {
    setFormData({ ...emptyForm });
    setEditingRecord(null);
    setFormTab(defaultFormTab);
    if (!keepModalOpen) setShowModal(false);
  };

  const openCreateModal = () => {
    const initial = { ...emptyForm };
    if (codeField && codeEditable) {
      initial[codeField] = generateCode();
    }
    if (requireEmployeeScope && apiQueryParams?.employee_id) {
      initial.employee_id = apiQueryParams.employee_id;
    }
    setFormData(initial);
    setEditingRecord(null);
    setFormTab(defaultFormTab);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requireEmployeeScope && !scopeReady) {
      notifyError(showNotify, pc('employee_scope_required', { defaultValue: 'تعذّر تحديد الموظف — أعد تسجيل الدخول' }));
      return;
    }
    const missingRequired = modalSections
      .flatMap((section) => section.fields || [])
      .filter((field) => {
        const label = p(field.labelKey);
        const required = field.required === true || /\*\s*$/.test(String(label).trim());
        if (!required || field.readOnly) return false;
        const val = formData[field.key];
        return val == null || String(val).trim() === '';
      });
    if (missingRequired.length) {
      notifyError(showNotify, pc('field_required', { defaultValue: 'يرجى تعبئة الحقول الإلزامية' }));
      return;
    }
    try {
      setLoading(true);
      let payload = toPayload(formData, editingRecord);
      if (requireEmployeeScope && apiQueryParams?.employee_id) {
        payload = { ...payload, employee_id: apiQueryParams.employee_id };
      }
      if (codeField && codeEditable) {
        const codeValue = (formData[codeField] || '').trim() || generateCode();
        payload = { ...payload, [codeField]: codeValue };
      }
      if (editingRecord?.id) {
        await localApiAdapter.put(`${apiPath}/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post(apiPath, payload);
        notifyCreated(showNotify, entityLabel);
      }
      resetForm();
      setSearchTerm('');
      setRefreshTrigger((n) => n + 1);
      notifyPortalDataChanged();
    } catch (error) {
      notifyError(showNotify, resolveApiError(error, pc('save_error')));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData(toForm(record));
    setFormTab(defaultFormTab);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(pc('delete_confirm'))) return;
    try {
      await localApiAdapter.delete(`${apiPath}/${id}`);
      notifyDeleted(showNotify, entityLabel);
      setRefreshTrigger((n) => n + 1);
      notifyPortalDataChanged();
    } catch (error) {
      notifyError(showNotify, resolveApiError(error, pc('delete_error')));
    }
  };

  const handleView = (record) => {
    setViewingRecord(record);
    setFormData(toForm(record));
    setShowViewModal(true);
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(pc('bulk_delete_confirm', { count: selectedRecords.length }))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`${apiPath}/${id}`)));
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      setSelectedRecords([]);
      setRefreshTrigger((n) => n + 1);
      notifyPortalDataChanged();
    } catch (error) {
      notifyError(showNotify, pc('delete_error'));
    }
  };

  const recordsArray = Array.isArray(records) ? records : [];
  const { dataColumns, statusColumn } = useMemo(
    () => partitionStatusFromColumns(columns),
    [columns],
  );
  const statCards = useMemo(() => sliceQawaemStats(computeStats(recordsArray), 4), [recordsArray, computeStats]);

  const normalizeStatusValue = useCallback(
    (val) => (normalizeStatus ? normalizeStatus(val) : val),
    [normalizeStatus],
  );

  const defaultStatusOptions = useMemo(() => ([
    { value: 'submitted', label: pl('svc_submitted'), icon: Clock, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'pending_manager', label: pl('svc_pending_manager'), icon: Clock, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'pending_hr', label: pl('svc_pending_hr'), icon: Clock, style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
    { value: 'in_progress', label: pl('svc_in_progress'), icon: Clock, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    { value: 'completed', label: pl('svc_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'rejected', label: pl('svc_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    { value: 'cancelled', label: pl('svc_cancelled'), icon: XCircle, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
    { value: 'approved', label: pl('leave_status_approved'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'actif', label: pl('doc_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'archive', label: pl('doc_archived'), icon: Tag, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
    { value: 'expire', label: pl('doc_expired'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    { value: 'brouillon', label: pl('ann_draft'), icon: Tag, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'publie', label: pl('ann_published'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'nouveau', label: pl('svc_submitted'), icon: Clock, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'en_cours', label: pl('svc_in_progress'), icon: Clock, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    { value: 'traite', label: pl('svc_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'rejete', label: pl('svc_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    { value: 'annule', label: pl('svc_cancelled'), icon: XCircle, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
    { value: 'en_attente', label: pl('leave_status_pending_manager'), icon: Clock, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'approuve', label: pl('leave_status_approved'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'planifie', label: pl('stat_planned'), icon: Clock, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    { value: 'termine', label: pl('stat_done'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'resilie', label: pl('stat_terminated'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ]), [pl]);

  const statusOptions = statusOptionsProp || (statusColumn ? defaultStatusOptions : []);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const payload = mapStatusToPayload
        ? mapStatusToPayload(newStatus)
        : { [statusField]: newStatus };
      await localApiAdapter.put(`${apiPath}/${recordId}`, payload);
      notifyUpdated(showNotify, entityLabel);
      setRefreshTrigger((n) => n + 1);
      notifyPortalDataChanged();
    } catch (error) {
      notifyError(showNotify, resolveApiError(error, pc('save_error')));
    }
  };

  const getFieldGridClass = (field) => {
    if (field.fullWidth || field.full || field.colSpan === 3 || field.type === 'textarea') return 'md:col-span-3';
    if (field.colSpan === 2) return 'md:col-span-2';
    return 'min-w-0';
  };

  const fieldLabel = (field) => {
    const base = p(field.labelKey);
    return field.required ? `${base} *` : base;
  };

  const qawaemColumnDefs = useMemo(
    () => {
      const defs = dataColumns.map((col) => ({
        key: col.key,
        label: col.labelKey ? p(col.labelKey) : col.label,
        checked: columnVisibility[col.key] !== false,
      }));
      if (statusColumn) {
        defs.push({
          key: statusColumn.key,
          label: statusColumn.labelKey ? p(statusColumn.labelKey) : statusColumn.label,
          checked: columnVisibility[statusColumn.key] !== false,
        });
      }
      return defs;
    },
    [dataColumns, statusColumn, columnVisibility, p],
  );

  const handleToggleColumn = (key) => {
    if (key === 'actions') return;
    const col = [...dataColumns, ...(statusColumn ? [statusColumn] : [])].find((c) => c.key === key);
    if (col?.required) return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;

  const visibleDataColumns = useMemo(
    () => dataColumns.filter((c) => columnVisibility[c.key] !== false),
    [dataColumns, columnVisibility],
  );
  const showStatusColumn = statusColumn && columnVisibility[statusColumn.key] !== false;
  const tableColSpan = visibleDataColumns.length + (showStatusColumn ? 1 : 0) + 2;

  const allFilterColumns = useMemo(
    () => [...dataColumns, ...(statusColumn ? [statusColumn] : [])],
    [dataColumns, statusColumn],
  );

  const columnFilterRules = useMemo(
    () => allFilterColumns.map((col) => {
      const getValue = col.filterGetValue || ((row) => {
        if (col.key === statusField) return String(normalizeStatusValue(row?.[col.key]) ?? '');
        return String(row?.[col.key] ?? '');
      });
      if (col.filterKind === 'select' || col.filterOptions?.length || col.key === statusField) {
        return exactColumnRule(col.key, getValue);
      }
      return textColumnRule(col.key, getValue);
    }),
    [allFilterColumns, statusField, normalizeStatusValue],
  );

  const searchFiltered = useMemo(
    () => recordsArray.filter((record) => {
      if (filterRecords && !filterRecords(record)) return false;
      if (!shouldShowCardsToolbarSearch(viewMode) || !searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return allFilterColumns.some((col) => {
        const raw = col.key === statusField
          ? normalizeStatusValue(record[col.key])
          : record[col.key];
        return raw != null && String(raw).toLowerCase().includes(q);
      });
    }),
    [recordsArray, filterRecords, searchTerm, viewMode, allFilterColumns, statusField, normalizeStatusValue],
  );

  const filtered = useMemo(
    () => applyColumnFilters(searchFiltered, columnFilters, columnFilterRules),
    [searchFiltered, columnFilters, columnFilterRules],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filtered);

  const handleExportCsv = () => {
    exportPortalCsv(filtered, columns, `${ns}-export.csv`);
    setExportOpen(false);
  };

  const renderCodeField = () => {
    if (!codeField || !codeEditable) return null;
    const value = formData[codeField] ?? editingRecord?.[codeField] ?? '';
    const inputCls = `${cfgTheme.input} font-mono`;
    return (
      <div key={codeField} className="min-w-0">
        <ErpFieldLabel
          label={p('field_code', { defaultValue: pc('field_code', { defaultValue: 'الرمز' }) })}
          className={erpFormLabelClass(isLight)}
        />
        <div data-testid={`employee-portal-field-${codeField}`}>
          <input
            data-testid={`employee-portal-input-${codeField}`}
            type="text"
            value={value}
            onChange={(e) => setFormData((prev) => ({ ...prev, [codeField]: e.target.value }))}
            className={inputCls}
            placeholder={p('placeholder_code', { defaultValue: pc('placeholder_code', { defaultValue: 'يُولَّد تلقائياً إن تُرك فارغاً' }) })}
          />
        </div>
      </div>
    );
  };

  const renderField = (field) => {
    if (codeField && field.key === codeField) return null;
    const value = formData[field.key] ?? '';
    const onChange = (v) => setFormData((prev) => ({ ...prev, [field.key]: v }));
    const inputCls = cfgTheme.input;
    const readOnlyCls = isLight
      ? 'w-full px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-600'
      : 'w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white/60';
    const textareaCls = isLight
      ? 'w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
      : 'w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500';

    if (field.readOnly) {
      if (field.type === 'select') {
        const opts = (field.options || []).map((o) => ({
          value: o.value,
          label: o.labelKey ? pl(o.labelKey) : (o.label || o.value),
        }));
        const label = opts.find((o) => o.value === value)?.label || value || '—';
        return (
          <div data-testid={`employee-portal-field-${field.key}`}>
            <input type="text" readOnly value={label} className={readOnlyCls} />
          </div>
        );
      }
      if (field.type === 'textarea') {
        return (
          <div data-testid={`employee-portal-field-${field.key}`}>
            <textarea readOnly value={value} rows={field.rows || 3} className={readOnlyCls} />
          </div>
        );
      }
      return (
        <div data-testid={`employee-portal-field-${field.key}`}>
          <input
            data-testid={`employee-portal-input-${field.key}`}
            type="text"
            value={value}
            readOnly
            className={readOnlyCls}
          />
        </div>
      );
    }
    if (field.type === 'select') {
      const opts = (field.options || []).map((o) => ({
        value: o.value,
        label: o.labelKey ? pl(o.labelKey) : (o.label || o.value),
      }));
      return (
        <div data-testid={`employee-portal-field-${field.key}`}>
          <CustomSelect
            dataTestId={`employee-portal-field-${field.key}`}
            value={value}
            onChange={onChange}
            options={opts}
            creatable={false}
            searchable
            disabled={!!field.disabled}
            placeholder={field.placeholder ? p(field.placeholder) : pc('select')}
          />
        </div>
      );
    }
    if (field.type === 'date') {
      return (
        <div data-testid={`employee-portal-field-${field.key}`}>
          <CustomDatePicker
            data-testid={`employee-portal-input-${field.key}`}
            value={value}
            onChange={onChange}
            placeholder={field.placeholder ? p(field.placeholder) : pc('select')}
          />
        </div>
      );
    }
    if (field.type === 'textarea') {
      return (
        <div data-testid={`employee-portal-field-${field.key}`}>
          <textarea
            data-testid={`employee-portal-input-${field.key}`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={field.rows || 3}
            className={textareaCls}
            placeholder={field.placeholder ? p(field.placeholder) : ''}
          />
        </div>
      );
    }
    return (
      <div data-testid={`employee-portal-field-${field.key}`}>
        <input
          data-testid={`employee-portal-input-${field.key}`}
          type={field.type || 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          placeholder={field.placeholder ? p(field.placeholder) : ''}
        />
      </div>
    );
  };

  const renderCell = (col, record) => {
    if (col.render) return col.render(record, p, t, pl);
    return record[col.key] ?? '—';
  };


  return (
    <>
    {scopeLoading ? (
      <p className="text-white/60 p-6">{t('common.loading')}</p>
    ) : null}
    {scopeBlocked ? (
      <>
        <EmployeePortalScopeAlert error={portal.resolveError} />
        <EmployeePortalUnlinkedState
          email={portal.email}
          name={portal.name}
          onLinked={() => setRefreshTrigger((n) => n + 1)}
        />
      </>
    ) : null}
    {!scopeBlocked && !scopeLoading ? (
    <QawaemErpListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={PageIcon}
      stats={statCards.map((stat) => ({
        key: stat.titleKey || stat.title,
        label: stat.titleKey ? p(stat.titleKey) : stat.title,
        value: stat.value,
        icon: stat.icon,
        variant: stat.variant || 'copper',
      }))}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={selectedRecords.length > 0 && allowDelete ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          title={pc('delete_selection')}
        >
          <Trash2 size={16} />
          <span>{t('common.delete')} ({selectedRecords.length})</span>
        </button>
      ) : null}
      searchTerm={searchTerm}
      onSearchChange={shouldShowCardsToolbarSearch(viewMode) ? setSearchTerm : undefined}
      searchPlaceholder={p('search_placeholder')}
      columns={qawaemColumnDefs}
      visibleColumnCount={qawaemVisibleColumnCount}
      totalColumnCount={qawaemColumnDefs.length}
      onCreate={allowCreate && scopeReady ? openCreateModal : null}
      createLabel={p('new_item')}
      headerActions={(
        <button
          type="button"
          onClick={() => setRefreshTrigger((n) => n + 1)}
          className="refresh-button"
          title={t('common.refresh')}
        >
          <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      )}
      importExport={{
        importOpen: false,
        exportOpen,
        onToggleImport: () => {},
        onToggleExport: () => setExportOpen((v) => !v),
        importRef: exportRef,
        exportRef,
        importLabel: pc('import'),
        exportLabel: pc('export'),
        generatingLabel: t('common.loading'),
        isGeneratingPDF: false,
        onImportCSV: () => {},
        onImportExcel: () => {},
        onExportPDF: () => {},
        onExportExcel: () => {},
        onExportCSV: handleExportCsv,
      }}
    >
      {viewMode === 'table' ? (
        <div className="glass-table-scroll overflow-x-auto">
          <table className="w-full employees-table data-table">
            <thead className="glass-table-head">
              <tr>
                <QawaemTableSelectAllHeader
                  allRowsSelected={allRowsSelected}
                  someRowsSelected={someRowsSelected}
                  onChange={handleSelectAll}
                  selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                  deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء التحديد' })}
                />
                {visibleDataColumns.map((col) => (
                  <TableColumnHeaderFilter
                    key={col.key}
                    columnKey={col.key}
                    label={col.labelKey ? p(col.labelKey) : col.label}
                    kind={col.filterKind || (col.filterOptions?.length ? 'select' : 'text')}
                    value={columnFilters[col.key] ?? ''}
                    onChange={(v) => setColumnFilter(col.key, v)}
                    options={(col.filterOptions || []).map((opt) => ({
                      value: opt.value,
                      label: opt.labelKey ? pl(opt.labelKey) : (opt.label || opt.value),
                    }))}
                    editingKey={columnFilterEditingKey}
                    onEditingKeyChange={setColumnFilterEditingKey}
                    thClassName="text-white font-semibold"
                    className="px-6 py-4"
                  />
                ))}
                {showStatusColumn ? (
                  <TableColumnHeaderFilter
                    columnKey={statusColumn.key}
                    label={statusColumn.labelKey ? p(statusColumn.labelKey) : statusColumn.label}
                    kind="select"
                    value={columnFilters[statusColumn.key] ?? ''}
                    onChange={(v) => setColumnFilter(statusColumn.key, v)}
                    options={statusOptions.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                    editingKey={columnFilterEditingKey}
                    onEditingKeyChange={setColumnFilterEditingKey}
                    align="center"
                    thClassName="text-white font-semibold"
                  />
                ) : null}
                <QawaemTableHeaderActionsCell
                  label={pc('actions')}
                  columns={qawaemColumnDefs}
                  onToggle={handleToggleColumn}
                  visibleCount={qawaemVisibleColumnCount}
                  totalCount={qawaemColumnDefs.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 employees-table-body">
              {loading ? (
                <tr>
                  <td colSpan={tableColSpan} className="qawaem-table-empty-cell p-12 text-center text-white/60">{t('common.loading')}</td>
                </tr>
              ) : filtered.length === 0 ? (
                <QawaemTableEmptyRow
                  colSpan={tableColSpan}
                  icon={PageIcon}
                  message={p('empty_title')}
                  hint={scopeBlocked ? '' : p('empty_hint')}
                />
              ) : filtered.map((record) => (
                <tr key={record.id} className="hover:bg-white/5" data-row-id={record.id}>
                  <QawaemTableSelectRowCell
                    checked={selectedRecords.includes(record.id)}
                    onChange={() => handleSelectRecord(record.id)}
                  />
                  {visibleDataColumns.map((col) => (
                    <td key={col.key} data-col-key={col.key} className="px-6 py-4 text-white/90 text-sm">{renderCell(col, record)}</td>
                  ))}
                  {showStatusColumn ? (
                    <td className="px-6 py-4 text-center align-middle">
                      <div className="flex justify-center">
                        <QawaemClickableStatusBadge
                          status={normalizeStatusValue(record[statusField])}
                          recordId={record.id}
                          options={statusOptions}
                          onChange={allowEdit ? handleStatusChange : null}
                          title={pc('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                        />
                      </div>
                    </td>
                  ) : null}
                  <td className="px-6 py-4 text-center align-middle">
                    <QawaemErpRowActions
                      onView={() => handleView(record)}
                      onEdit={allowEdit ? () => handleEdit(record) : null}
                      onDelete={allowDelete ? () => handleDelete(record.id) : null}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : loading ? (
        <div className="p-12 text-center text-white/60">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center">
          <PageIcon className="h-12 w-12 text-white/30 mx-auto mb-4" />
          <p className="text-white/60">{p('empty_title')}</p>
          {!scopeBlocked ? (
            <p className="text-white/40 text-sm mt-1">{p('empty_hint')}</p>
          ) : null}
        </div>
      ) : (
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
          {filtered.map((record) => (
            <div key={record.id} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 h-full min-w-0">
              <div className="flex justify-between items-start mb-3">
                <div className="min-w-0">
                  <p className="text-white font-semibold">{record.name || record.title || record[codeField]}</p>
                  <p className="text-white/50 text-xs mt-1">{record[codeField] || record.workflow_code || record.request_code || record.template_code}</p>
                </div>
                <QawaemErpRowActions
                  compact
                  size={14}
                  onView={() => handleView(record)}
                  onEdit={allowEdit ? () => handleEdit(record) : null}
                  onDelete={allowDelete ? () => handleDelete(record.id) : null}
                  viewTitle={t('common.view')}
                  editTitle={t('common.edit')}
                  deleteTitle={t('common.delete')}
                />
              </div>
              {statusColumn ? (
                <div className="mb-2">
                  <QawaemClickableStatusBadge
                    status={normalizeStatusValue(record[statusField])}
                    recordId={record.id}
                    options={statusOptions}
                    onChange={allowEdit ? handleStatusChange : null}
                  />
                </div>
              ) : null}
              {dataColumns.slice(1, 4).map((col) => (
                <p key={col.key} className="text-white/60 text-sm">
                  <span className="text-white/40">{col.labelKey ? p(col.labelKey) : col.label}: </span>
                  {renderCell(col, record)}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
    </QawaemErpListPageShell>
    ) : null}

      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingRecord ? p('modal_edit') : p('modal_new')}
        icon={PageIcon}
        showSaveButton={allowCreate || !!editingRecord}
        formId="employee-portal-form"
        saveLabel={t('common.save')}
        saving={loading}
        testId="employee-portal-form-modal"
        tabs={showFormTabs ? formTabs : undefined}
        activeTab={formTab}
        onTabChange={setFormTab}
        tabsAriaLabel={editingRecord ? p('modal_edit') : p('modal_new')}
      >
        <form id="employee-portal-form" data-testid="employee-portal-form" className={ERP_MODAL_FORM_CLASS} onSubmit={handleSubmit}>
          {modalSections.map((section, sectionIndex) => (!showFormTabs || formTab === section.id) && (
            <div key={section.id} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {sectionIndex === 0 && renderCodeField()}
              {section.fields.map((field) => (
                <div key={field.key} className={getFieldGridClass(field)}>
                  <ErpFieldLabel label={fieldLabel(field)} className={erpFormLabelClass(isLight)} />
                  {renderField(field)}
                </div>
              ))}
            </div>
          ))}
        </form>
      </QawaemErpCrudModal>

      {showViewModal && viewingRecord ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => { setShowViewModal(false); setViewingRecord(null); }}
          title={p('view_title')}
          icon={FileSearch}
          testId="portal-view-modal"
        >
          <div data-portal-view-modal="true" className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {columns.map((col) => (
              <div key={col.key} className={`min-w-0 ${col.key === 'description' || col.key === 'content' || col.key === 'notes' ? 'md:col-span-3' : ''}`}>
                <ErpFieldLabel
                  label={col.labelKey ? p(col.labelKey) : col.label}
                  className={erpFormLabelClass(isLight)}
                />
                <div className={`text-sm mt-1 ${cfgTheme.title}`}>{renderCell(col, viewingRecord)}</div>
              </div>
            ))}
            {modalSections.flatMap((section) => section.fields
              .filter((f) => !columns.some((c) => c.key === f.key))
              .map((field) => (
                <div key={field.key} className={getFieldGridClass(field)}>
                  <ErpFieldLabel label={fieldLabel(field)} className={erpFormLabelClass(isLight)} />
                  <div className={`text-sm mt-1 whitespace-pre-wrap ${cfgTheme.title}`}>
                    {formData[field.key] ?? '—'}
                  </div>
                </div>
              )))}
          </div>
        </QawaemErpViewModal>
      ) : null}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
}
